import { useCallback, useState } from 'react';
import { primeCustomModel } from '../../../potree/sketchObjects/custom/modelCache';
import { fitModelPlacement, upAxisRotation } from '../../../potree/sketchObjects/custom/modelPlacement';
import { ObjectDefinitionAssetKind } from '../../../types/graphqlTypes';
import { useObjectEditor } from '../EditorContext';
import { useUploadObjectAsset } from '../useUploadObjectAsset';
import { exportGlb, loadModelFile, TRIANGLE_WARNING } from './loadModelFile';

export interface ModelImportState {
  busy: boolean;
  error: string | null;
  /** Triangle count when it is high enough to slow projects down. */
  heavyTriangles: number | null;
}

/**
 * Add (or replace) the object's 3D model from a file: read it (any supported
 * format), store it as GLB (+ the original as a source asset), and place it
 * standing on the footprint, scaled to fit the default size.
 */
export function useModelImport() {
  const { store, organisationId, definition } = useObjectEditor();
  const upload = useUploadObjectAsset(organisationId, definition.id);
  const [state, setState] = useState<ModelImportState>({ busy: false, error: null, heavyTriangles: null });

  const importModel = useCallback(
    async (file: File) => {
      setState({ busy: true, error: null, heavyTriangles: null });
      try {
        const loaded = await loadModelFile(file);
        const glb = await exportGlb(loaded.object);
        const baseName = file.name.replace(/\.[^.]+$/, '');
        const asset = await upload(glb, `${baseName}.glb`, ObjectDefinitionAssetKind.ModelGlb, 'glb');
        primeCustomModel(asset.id, loaded.object);
        const placement = fitModelPlacement(loaded.bounds, upAxisRotation(loaded.yUp), store.getState().spec.defaultSize);
        store.apply((spec) => ({ ...spec, model3d: { assetId: asset.id, ...placement, scaleMode: spec.model3d?.scaleMode ?? { u: 'stretch', n: 'stretch', h: 'stretch' } } }));
        // The original file is kept for later re-conversion; the model works without it.
        if (!file.name.toLowerCase().endsWith('.glb')) void upload(file, file.name, ObjectDefinitionAssetKind.Source_3D, file.name.split('.').pop() ?? 'bin').catch(() => undefined);
        setState({ busy: false, error: null, heavyTriangles: loaded.triangles > TRIANGLE_WARNING ? loaded.triangles : null });
      } catch (e) {
        setState({ busy: false, error: e instanceof Error ? e.message : String(e), heavyTriangles: null });
      }
    },
    [store, upload],
  );

  const removeModel = useCallback(() => store.apply((spec) => ({ ...spec, model3d: null })), [store]);

  return { ...state, importModel, removeModel, dismissError: () => setState((s) => ({ ...s, error: null })) };
}
